class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (event.target.closest('[data-ad-collection-search]')) return;
      this.debouncedOnSubmit(event);
    });
    FacetFiltersForm.bindAdCollectionSearches(this);

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static bindAdCollectionSearches(scope = document) {
    scope.querySelectorAll('[data-ad-collection-search]').forEach((searchRoot) => {
      if (searchRoot.dataset.adCollectionSearchBound === 'true') return;
      searchRoot.dataset.adCollectionSearchBound = 'true';

      const input = searchRoot.querySelector('[data-ad-collection-search-input]');
      const button = searchRoot.querySelector('[data-ad-collection-search-submit]');
      if (!input || !button) return;

      FacetFiltersForm.ensureAdCollectionSearchStatus(searchRoot);

      const submitSearch = () => {
        FacetFiltersForm.loadAdCollectionSearchResults(searchRoot, input.value);
      };

      button.addEventListener('click', submitSearch);
      input.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        submitSearch();
      });
      input.addEventListener('input', () => {
        if (input.value.trim() || !FacetFiltersForm.adCollectionSearchState?.active) return;
        FacetFiltersForm.restoreAdCollectionSearchBaseline();
      });
    });

    FacetFiltersForm.bindAdCollectionPagination();
  }

  static getAdCollectionSearchRoots() {
    return Array.from(document.querySelectorAll('[data-ad-collection-search]'));
  }

  static ensureAdCollectionSearchStatus(searchRoot) {
    if (searchRoot.querySelector('[data-ad-collection-search-status]')) return;

    const status = document.createElement('p');
    status.className = 'ad-collection-search__status';
    status.dataset.adCollectionSearchStatus = '';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    searchRoot.appendChild(status);
  }

  static bindAdCollectionPagination() {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer || gridContainer.dataset.adCollectionSearchPaginationBound === 'true') return;

    gridContainer.dataset.adCollectionSearchPaginationBound = 'true';
    gridContainer.addEventListener('click', (event) => {
      const state = FacetFiltersForm.adCollectionSearchState;
      const paginationLink = event.target.closest('.pagination a');
      if (!state?.active || !paginationLink || !gridContainer.contains(paginationLink)) return;

      const paginationUrl = new URL(paginationLink.href, window.location.origin);
      if (paginationUrl.searchParams.get('view') !== 'blanket-products') return;

      event.preventDefault();
      FacetFiltersForm.loadAdCollectionSearchResults(null, null, paginationLink.href);
    });
  }

  static setAdCollectionSearchStatus(message, isError = false) {
    FacetFiltersForm.getAdCollectionSearchRoots().forEach((searchRoot) => {
      FacetFiltersForm.ensureAdCollectionSearchStatus(searchRoot);
      const status = searchRoot.querySelector('[data-ad-collection-search-status]');
      status.textContent = message;
      status.classList.toggle('ad-collection-search__status--error', isError);
    });
  }

  static setAdCollectionSearchLoading(isLoading) {
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      gridContainer.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      const collection = gridContainer.querySelector('.collection');
      if (collection) collection.classList.toggle('loading', isLoading);
    }

    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((count) => {
      count.classList.toggle('loading', isLoading);
    });
    FacetFiltersForm.getAdCollectionSearchRoots().forEach((searchRoot) => {
      searchRoot.setAttribute('aria-busy', isLoading ? 'true' : 'false');
      searchRoot.querySelectorAll('[data-ad-collection-search-submit]').forEach((button) => {
        button.disabled = isLoading;
      });
    });
  }

  static syncAdCollectionSearchInputs(value) {
    FacetFiltersForm.getAdCollectionSearchRoots().forEach((searchRoot) => {
      const input = searchRoot.querySelector('[data-ad-collection-search-input]');
      if (input && input.value !== value) input.value = value;
    });
  }

  static endAdCollectionSearch() {
    const state = FacetFiltersForm.adCollectionSearchState;
    if (!state?.active) return;

    if (state.controller) state.controller.abort();
    state.generation += 1;
    state.active = false;
    state.controller = null;
    FacetFiltersForm.adCollectionSearchState = null;
    FacetFiltersForm.syncAdCollectionSearchInputs('');
    FacetFiltersForm.setAdCollectionSearchLoading(false);
    FacetFiltersForm.setAdCollectionSearchStatus('Showing collection results.');
  }

  static cacheAdCollectionSearchBaseline() {
    if (FacetFiltersForm.adCollectionSearchState) return;

    const gridContainer = document.getElementById('ProductGridContainer');
    if (!gridContainer) return;

    const counts = {};
    document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((count) => {
      counts[count.id] = count.innerHTML;
    });

    FacetFiltersForm.adCollectionSearchState = {
      active: false,
      term: '',
      generation: 0,
      gridHTML: gridContainer.innerHTML,
      counts,
      controller: null,
    };
  }

  static restoreAdCollectionSearchBaseline() {
    const state = FacetFiltersForm.adCollectionSearchState;
    const gridContainer = document.getElementById('ProductGridContainer');
    if (!state || !gridContainer) return;

    if (state.controller) state.controller.abort();
    state.generation += 1;
    gridContainer.innerHTML = state.gridHTML;
    Object.entries(state.counts).forEach(([id, html]) => {
      const count = document.getElementById(id);
      if (count) count.innerHTML = html;
    });
    state.active = false;
    state.term = '';
    state.controller = null;
    FacetFiltersForm.adCollectionSearchState = null;
    FacetFiltersForm.syncAdCollectionSearchInputs('');
    FacetFiltersForm.setAdCollectionSearchLoading(false);
    FacetFiltersForm.setAdCollectionSearchStatus('Showing all blanket designs.');
    FacetFiltersForm.bindAdCollectionPagination();
  }

  static getAdCollectionSearchUrl(searchRoot, searchTerm, href = '') {
    const url = new URL(href || searchRoot.dataset.searchUrl || '/search', window.location.origin);
    if (!href) {
      const baseTerms = (searchRoot.dataset.searchBaseTerms || '').trim();
      const escapedBaseTerms = baseTerms.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
      const basePhrase = escapedBaseTerms ? `"${escapedBaseTerms}"` : '';
      const query = [basePhrase, searchTerm].filter(Boolean).join(' AND ');
      url.searchParams.set('q', query);
    }

    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    const searchView = (searchRoot.dataset.searchView || '').trim();
    if (searchView) url.searchParams.set('view', searchView);
    return url.toString();
  }

  static finalizeAdCollectionSearchGrid(gridContainer) {
    gridContainer.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
    if (typeof initializeScrollAnimationTrigger === 'function' && typeof IntersectionObserver === 'function') {
      initializeScrollAnimationTrigger(gridContainer);
    }
  }

  static loadAdCollectionSearchResults(searchRoot, rawTerm, href = '') {
    const state = FacetFiltersForm.adCollectionSearchState;
    const input = searchRoot?.querySelector('[data-ad-collection-search-input]');
    const searchTerm = (rawTerm ?? input?.value ?? '').trim().replace(/\s+/g, ' ');

    if (!href && !searchTerm) {
      if (state) {
        FacetFiltersForm.restoreAdCollectionSearchBaseline();
      } else {
        FacetFiltersForm.syncAdCollectionSearchInputs('');
        FacetFiltersForm.setAdCollectionSearchStatus('Showing all blanket designs.');
      }
      return;
    }

    FacetFiltersForm.cacheAdCollectionSearchBaseline();
    const currentState = FacetFiltersForm.adCollectionSearchState;
    if (!currentState) return;
    if (currentState.controller) currentState.controller.abort();

    const sourceRoot = searchRoot || FacetFiltersForm.getAdCollectionSearchRoots()[0];
    if (!sourceRoot) return;
    if (!href) FacetFiltersForm.syncAdCollectionSearchInputs(searchTerm);

    const requestUrl = FacetFiltersForm.getAdCollectionSearchUrl(sourceRoot, searchTerm, href);
    const controller = typeof AbortController === 'function' ? new AbortController() : null;
    currentState.generation += 1;
    const requestGeneration = currentState.generation;
    currentState.controller = controller;
    currentState.active = true;
    if (!href) currentState.term = searchTerm;
    FacetFiltersForm.setAdCollectionSearchLoading(true);
    FacetFiltersForm.setAdCollectionSearchStatus(
      href ? 'Loading more blanket designs…' : `Searching blanket designs for “${searchTerm}”…`
    );

    fetch(requestUrl, {
      signal: controller?.signal,
      headers: { Accept: 'text/html' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Search request failed with status ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (
          currentState !== FacetFiltersForm.adCollectionSearchState ||
          requestGeneration !== currentState.generation
        ) {
          return;
        }

        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const fetchedGrid = parsedHTML.getElementById('ProductGridContainer');
        if (!fetchedGrid) throw new Error('Search response did not contain a product grid');

        const gridContainer = document.getElementById('ProductGridContainer');
        if (!gridContainer) throw new Error('Current page does not contain a product grid');
        gridContainer.innerHTML = fetchedGrid.innerHTML;
        FacetFiltersForm.finalizeAdCollectionSearchGrid(gridContainer);

        const fetchedCount = parsedHTML.querySelector('#ProductCount, #ProductCountDesktop');
        if (fetchedCount) {
          document.querySelectorAll('#ProductCount, #ProductCountDesktop').forEach((count) => {
            count.innerHTML = fetchedCount.innerHTML;
          });
        }

        currentState.active = true;
        currentState.controller = null;
        FacetFiltersForm.setAdCollectionSearchLoading(false);
        FacetFiltersForm.setAdCollectionSearchStatus(
          fetchedGrid.querySelectorAll('#product-grid .grid__item').length
            ? `Showing blanket designs matching “${currentState.term || 'your search'}”.`
            : `No blanket designs matched “${currentState.term || 'your search'}”.`
        );
        FacetFiltersForm.bindAdCollectionPagination();
      })
      .catch((error) => {
        if (
          error.name === 'AbortError' ||
          currentState !== FacetFiltersForm.adCollectionSearchState ||
          requestGeneration !== currentState.generation
        ) {
          return;
        }

        currentState.controller = null;
        FacetFiltersForm.setAdCollectionSearchLoading(false);
        FacetFiltersForm.setAdCollectionSearchStatus(
          'Search could not be completed. Your current products are still shown; please try again.',
          true
        );
      });
  }

  static getDefaultSortBy() {
    return document.querySelector('[data-ad-default-sort-by]')?.dataset.adDefaultSortBy || '';
  }

  static applyDefaultSortBy(searchParams) {
    const defaultSortBy = FacetFiltersForm.getDefaultSortBy();
    if (!defaultSortBy) return searchParams;

    const params = new URLSearchParams(searchParams);
    if (!params.has('sort_by')) params.set('sort_by', defaultSortBy);
    return params.toString();
  }

  static clearDependentSubTheme(searchParams, event) {
    const inputName = event?.target?.name?.toLowerCase() || '';
    const isThemeCategoryInput = inputName.includes('filter.p.m.') && inputName.includes('.category');
    if (!isThemeCategoryInput) return searchParams;

    const params = new URLSearchParams(searchParams);
    params.delete('filter.p.tag');
    return params.toString();
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.endAdCollectionSearch();
    searchParams = FacetFiltersForm.applyDefaultSortBy(searchParams);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    FacetFiltersForm.endAdCollectionSearch();
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    FacetFiltersForm.endAdCollectionSearch();
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    const adHiddenInputSelectors = [
      '#FacetFiltersForm .ad-collection-hidden-inputs',
      '#FacetFiltersFormMobile .ad-collection-hidden-inputs',
    ];

    adHiddenInputSelectors.forEach((selector) => {
      const currentHiddenInputs = document.querySelector(selector);
      const hiddenInputsFromFetch = html.querySelector(selector);

      if (currentHiddenInputs && hiddenInputsFromFetch) {
        currentHiddenInputs.innerHTML = hiddenInputsFromFetch.innerHTML;
      }
    });

    const adThemeCategoryFilter = document.querySelector('.ad-theme-category-filter');
    const adThemeCategoryFilterFromFetch = html.querySelector('.ad-theme-category-filter');

    if (adThemeCategoryFilter && adThemeCategoryFilterFromFetch) {
      adThemeCategoryFilter.outerHTML = adThemeCategoryFilterFromFetch.outerHTML;
    }

    const adMobileSort = document.querySelector('#SortBy-mobile-ad');
    const adMobileSortFromFetch = html.querySelector('#SortBy-mobile-ad');

    if (adMobileSort && adMobileSortFromFetch) {
      adMobileSort.value = adMobileSortFromFetch.value;
    }

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const currentForm = event.target.closest('form');

    if (currentForm?.id === 'FacetSortFormMobileAd') {
      const searchParams = new URLSearchParams(window.location.search);
      searchParams.set('sort_by', currentForm.elements.sort_by.value);
      searchParams.delete('page');
      this.onSubmitForm(searchParams.toString(), event);
      return;
    }

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = FacetFiltersForm.clearDependentSubTheme(
        this.createSearchParams(event.target.closest('form')),
        event
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = currentForm?.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(FacetFiltersForm.clearDependentSubTheme(forms.join('&'), event), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    let href = event.currentTarget.href;

    if (event.currentTarget.matches('.ad-theme-category-filter__chip, .ad-theme-category-filter__clear')) {
      const url = new URL(href);
      url.searchParams.delete('filter.p.tag');
      href = url.toString();
    }

    const url =
      href.indexOf('?') == -1
        ? ''
        : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
FacetFiltersForm.bindAdCollectionSearches();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
